"use client";

import { useEffect, useState } from "react";
import { formatDuration } from "@creators/backend/lib/time";
import { cx, Pill } from "./ui";

/**
 * A live countdown to an auto-approval or expiry deadline.
 *
 * Both parties see this everywhere the collab appears — it is what stops creators
 * chasing brands and what makes brands actually review. The first paint uses the
 * server-computed label so there is no hydration mismatch, then it ticks.
 */
export function Countdown({
  deadline,
  initialLabel,
  prefix,
  className,
}: {
  deadline: string;
  initialLabel: string;
  prefix?: string;
  className?: string;
}) {
  const [label, setLabel] = useState(initialLabel);
  const [overdue, setOverdue] = useState(false);

  useEffect(() => {
    const target = new Date(deadline).getTime();

    const tick = () => {
      const ms = target - Date.now();
      setLabel(formatDuration(ms));
      setOverdue(ms <= 0);
    };

    tick();
    // Tick every second inside the last hour, otherwise every half minute.
    const remaining = target - Date.now();
    const interval = setInterval(tick, remaining < 3_600_000 ? 1_000 : 30_000);
    return () => clearInterval(interval);
  }, [deadline]);

  return (
    <span className={cx("tabular", className)}>
      {overdue ? "any moment now" : `${prefix ? `${prefix} ` : ""}${label}`}
    </span>
  );
}

export function CountdownPill({
  deadline,
  initialLabel,
  action,
  tone = "amber",
}: {
  deadline: string;
  initialLabel: string;
  /** What happens when it runs out, e.g. "Auto-approves". */
  action: string;
  tone?: "amber" | "blue" | "green" | "neutral";
}) {
  return (
    <Pill tone={tone} dot={false}>
      <ClockIcon />
      <Countdown deadline={deadline} initialLabel={initialLabel} prefix={`${action} in`} />
    </Pill>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 4.75V8l2.25 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
